import { Children, memo, useCallback, useState, type ReactNode } from 'react'
import { Button } from '@highlight/ui'
import { Mic, CornerDownLeft, MessageSquarePlus } from 'lucide-react'
import { HighlightedFragment } from '@renderer/windows/features/context/components/search-content'
import { useDebouncedValue } from '@renderer/windows/features/people/hooks/use-debounced-value'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { formatTimeAgo } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { SidepanelCellRow } from '../cells/row'
import { HistoryListSkeleton } from '../history/history-list-skeleton'
import { HistoryRow } from '../history/history-row'
import { useOpenActivity } from '../history/use-open-activity'
import { ViewGroupLabel } from '../view-group-label'
import { useSidebarSearchQuery } from './sidebar-search-atoms'
import { useAudioNoteSearch, type AudioNoteMatch } from './use-audio-note-search'
import { useAudioNoteTitle } from './use-audio-note-title'
import { useRecentChatSearch } from './use-recent-chat-search'
import { useStartChatAboutQuery } from './use-start-chat-about-query'

const SEARCH_DEBOUNCE_MS = 200

/** Rows a group shows before its View all. */
const GROUP_PREVIEW_LIMIT = 5

const GLYPH_CLASS = 'text-foreground-secondary flex size-5 shrink-0 items-center justify-center [&_svg]:size-4'

/** Takes Home's place while the search bar holds text: matching meetings and chats, then a row to chat about the query. */
export function SidebarSearchResults() {
  const [query] = useSidebarSearchQuery()
  const settledQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS)
  const audioNoteSearch = useAudioNoteSearch(settledQuery)
  const chatSearch = useRecentChatSearch(query)
  const openActivity = useOpenActivity('Search')

  return (
    <div className="flex flex-col gap-[2px] px-1 py-3">
      <SearchResultGroup
        key={`meetings-${settledQuery}`}
        label="Meetings"
        isFirst
        isLoading={audioNoteSearch.isLoading}
        hasError={!!audioNoteSearch.error}
        isEmpty={audioNoteSearch.matches.length === 0}
        emptyMessage={
          audioNoteSearch.emptyReason === 'no-ingestion-namespace' ||
          audioNoteSearch.emptyReason === 'namespace-not-found'
            ? 'Your meetings aren’t searchable yet.'
            : 'No meetings match that.'
        }
      >
        {audioNoteSearch.matches.map((match) => (
          <AudioNoteResultRow key={match.audioNoteId} match={match} />
        ))}
      </SearchResultGroup>
      <SearchResultGroup
        key={`chats-${settledQuery}`}
        label="Chats"
        isLoading={chatSearch.isLoading}
        isEmpty={chatSearch.matches.length === 0}
        emptyMessage="No recent chats match that."
      >
        {chatSearch.matches.map((match) => (
          <HistoryRow
            key={match.item.id}
            item={match.item}
            onOpen={openActivity}
            subtitle={
              match.snippet && (
                <div className="line-clamp-2">
                  <HighlightedFragment highlight={match.snippet} />
                </div>
              )
            }
          />
        ))}
      </SearchResultGroup>
      <ChatAboutQueryRow query={query.trim()} />
    </div>
  )
}

interface SearchResultGroupProps {
  label: string
  isFirst?: boolean
  isLoading: boolean
  hasError?: boolean
  isEmpty: boolean
  emptyMessage: string
  children: ReactNode
}

/**
 * Each group loads and fails on its own, so a slow or broken search doesn't hide the other's results. A group
 * shows its first few rows, and View all shows the rest in place. Keyed by the query, so a new query starts collapsed.
 */
function SearchResultGroup({
  label,
  isFirst,
  isLoading,
  hasError,
  isEmpty,
  emptyMessage,
  children,
}: SearchResultGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const rows = Children.toArray(children)
  const hiddenCount = isExpanded ? 0 : Math.max(rows.length - GROUP_PREVIEW_LIMIT, 0)

  return (
    <>
      <ViewGroupLabel isFirst={isFirst}>{label}</ViewGroupLabel>
      {isLoading ? (
        <HistoryListSkeleton count={3} />
      ) : hasError ? (
        <SearchMessage>Search isn’t working right now. Try again in a moment.</SearchMessage>
      ) : isEmpty ? (
        <SearchMessage>{emptyMessage}</SearchMessage>
      ) : (
        <>
          {hiddenCount > 0 ? rows.slice(0, GROUP_PREVIEW_LIMIT) : rows}
          {hiddenCount > 0 && (
            <Button
              variant="ghost"
              size="xs"
              className="mx-2 self-start"
              aria-label={`View all ${rows.length} ${label.toLowerCase()}`}
              onClick={() => setIsExpanded(true)}
            >
              View all {rows.length}
            </Button>
          )}
        </>
      )}
    </>
  )
}

function SearchMessage({ children }: { children: string }) {
  return <p className="text-foreground-secondary type-ui-sm px-3 py-2">{children}</p>
}

function ResultTimestamp({ at }: { at: string }) {
  return (
    <span className="text-foreground-secondary type-ui-xs shrink-0 whitespace-nowrap pt-[2px] font-normal">
      {formatTimeAgo(at)}
    </span>
  )
}

const AudioNoteResultRow = memo(function AudioNoteResultRow({ match }: { match: AudioNoteMatch }) {
  const title = useAudioNoteTitle(match.audioNoteId) ?? 'Meeting'
  const { openView } = useViewActions()

  const openAudioNote = useCallback(() => {
    openView(MEETING_VIEW_ID, { mode: 'note', audioNoteId: match.audioNoteId, title }, { sourceLabel: 'Search' })
  }, [openView, match.audioNoteId, title])

  return (
    <SidepanelCellRow
      icon={
        <div className={GLYPH_CLASS}>
          <Mic />
        </div>
      }
      title={title}
      subtitle={
        match.highlight && (
          <div className="line-clamp-2">
            <HighlightedFragment highlight={match.highlight} />
          </div>
        )
      }
      trailing={match.happenedAt && <ResultTimestamp at={match.happenedAt} />}
      ariaLabel={title}
      className="px-3 py-2"
      onClick={openAudioNote}
    />
  )
})

function ChatAboutQueryRow({ query }: { query: string }) {
  const startChatAboutQuery = useStartChatAboutQuery()
  return (
    <SidepanelCellRow
      icon={
        <div className={GLYPH_CLASS}>
          <MessageSquarePlus />
        </div>
      }
      title={`Chat about “${query}”`}
      align="center"
      trailing={<CornerDownLeft className="text-foreground-secondary size-3.5 shrink-0" aria-hidden="true" />}
      ariaLabel={`Chat about ${query}`}
      className="mt-2 px-3 py-2"
      onClick={startChatAboutQuery}
    />
  )
}
